import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useData } from '../contexts/DataContext';
import { getDisplayName } from '../utils/displayHelpers';
import { Search, Filter, MapPin, Star, Clock, X, Sliders, ChevronDown } from 'lucide-react';
import CaregiverCard from '../components/CaregiverCard';
import AnimatedEmptyState from '../components/ui/AnimatedEmptyState';
import DataFetchState from '../components/ui/DataFetchState';
import ScrollReveal from '../components/ui/ScrollReveal';
import ScrollRevealStack from '../components/ui/ScrollRevealStack';
import { SERVICE_CATALOG, getServiceLabel, toServiceSlug } from '../config/services';

const SearchCaregivers = () => {
  const { caregivers, caregiversLoading, caregiversError, fetchCaregivers } = useData();
  const [searchParams, setSearchParams] = useSearchParams();
  const serviceFilter = toServiceSlug(searchParams.get('service'));

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecies, setSelectedSpecies] = useState('');
  const [priceRange, setPriceRange] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [filteredCaregivers, setFilteredCaregivers] = useState(caregivers);
  const [showFilters, setShowFilters] = useState(false);
  const [location, setLocation] = useState('');

  const setServiceFilter = useCallback((slug) => {
    const params = new URLSearchParams(searchParams);
    if (slug) {
      params.set('service', slug);
    } else {
      params.delete('service');
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const speciesOptions = ['Dog', 'Cat', 'Bird', 'Other'];
  const priceRanges = [
    { label: 'Under ₹200/hr', value: 'under-200' },
    { label: '₹200-300/hr', value: '200-300' },
    { label: '₹300-400/hr', value: '300-400' },
    { label: 'Above ₹400/hr', value: 'above-400' }
  ];

  const sortOptions = [
    { label: 'Highest Rated', value: 'rating' },
    { label: 'Price: Low to High', value: 'price-low' },
    { label: 'Price: High to Low', value: 'price-high' },
    { label: 'Most Reviews', value: 'reviews' }
  ];

  useEffect(() => {
    // Ensure caregivers is an array
    if (!Array.isArray(caregivers)) {
      setFilteredCaregivers([]);
      return;
    }

    let filtered = [...caregivers];

    // Filter by the service the owner selected (hero dropdown / nav / URL)
    if (serviceFilter) {
      filtered = filtered.filter(caregiver =>
        Array.isArray(caregiver.serviceSlugs) && caregiver.serviceSlugs.includes(serviceFilter)
      );
    }

    // Search by name or title
    if (searchTerm) {
      filtered = filtered.filter(caregiver => {
        const name = caregiver.user?.name || '';
        const title = caregiver.title || '';
        const bio = caregiver.bio || '';
        const searchLower = searchTerm.toLowerCase();
        
        return name.toLowerCase().includes(searchLower) ||
               title.toLowerCase().includes(searchLower) ||
               bio.toLowerCase().includes(searchLower);
      });
    }

    // Filter by species
    if (selectedSpecies) {
      filtered = filtered.filter(caregiver =>
        caregiver.speciesAccepted?.includes(selectedSpecies)
      );
    }

    // Filter by price range
    if (priceRange) {
      filtered = filtered.filter(caregiver => {
        const rate = caregiver.hourlyRate || 0;
        switch (priceRange) {
          case 'under-200':
            return rate < 200;
          case '200-300':
            return rate >= 200 && rate <= 300;
          case '300-400':
            return rate >= 300 && rate <= 400;
          case 'above-400':
            return rate > 400;
          default:
            return true;
        }
      });
    }

    // Filter by location (caregiver profile location)
    if (location.trim()) {
      const locLower = location.trim().toLowerCase();
      filtered = filtered.filter((caregiver) => {
        const caregiverLocation = (
          caregiver.location ||
          caregiver.user?.location ||
          ''
        ).toLowerCase();
        return caregiverLocation.includes(locLower);
      });
    }

    // Sort results
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'price-low':
          return (a.hourlyRate || 0) - (b.hourlyRate || 0);
        case 'price-high':
          return (b.hourlyRate || 0) - (a.hourlyRate || 0);
        case 'reviews':
          return (b.reviewCount || 0) - (a.reviewCount || 0);
        default:
          return 0;
      }
    });

    setFilteredCaregivers(filtered);
  }, [searchTerm, selectedSpecies, priceRange, sortBy, caregivers, serviceFilter, location]);

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSpecies('');
    setPriceRange('');
    setSortBy('rating');
    setLocation('');
    setServiceFilter('');
  };

  return (
    <div className="pf-page-wrap pb-32 lg:pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <ScrollReveal direction="left" className="mb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
            {serviceFilter
              ? `${getServiceLabel(serviceFilter)} Caregivers`
              : 'Browse Local Pet Caregivers'}
          </h1>
          <p className="text-gray-600">
            {serviceFilter
              ? `Caregivers in your area offering ${getServiceLabel(serviceFilter).toLowerCase()}`
              : 'Connect with independent caregivers in your area offering pet care services'}
          </p>
        </ScrollReveal>

        {/* Search Bar */}
        <ScrollReveal direction="right">
          <div className="pf-glass pf-glass--panel pf-glass--sticky-bar mb-8">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Service */}
            <div className="flex-1 form-group">
              <label htmlFor="search-service" className="form-label">Service</label>
              <div className="relative input-icon-wrap">
                <Sliders className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <select
                  id="search-service"
                  value={serviceFilter}
                  onChange={(e) => setServiceFilter(e.target.value)}
                  aria-label="Filter by service"
                  className="form-input"
                >
                  <option value="">All Services</option>
                  {SERVICE_CATALOG.map((service) => (
                    <option key={service.slug} value={service.slug}>{service.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Location */}
            <div className="flex-1 form-group">
              <label htmlFor="search-location" className="form-label">Location</label>
              <div className="relative input-icon-wrap">
                <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="search-location"
                  type="text"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="form-input"
                  placeholder="Enter your location"
                />
              </div>
            </div>

            {/* Search */}
            <div className="flex-1 form-group">
              <label htmlFor="search-term" className="form-label">Search</label>
              <div className="relative input-icon-wrap">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  id="search-term"
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input"
                  placeholder="Search by name or services..."
                />
              </div>
            </div>

            {/* Filter Button */}
            <div className="form-group lg:pt-6">
              <label className="form-label hidden lg:block">&nbsp;</label>
              <button
                type="button"
                onClick={() => setShowFilters(!showFilters)}
                className={`btn-outline w-full lg:w-auto ${showFilters ? 'btn-outline-active' : ''}`}
                aria-expanded={showFilters}
              >
                <Sliders className="w-5 h-5" />
                Filters
                <ChevronDown className={`w-5 h-5 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-6 pt-6 border-t border-[var(--pf-border)] grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Species Filter */}
              <div className="form-group">
                <label htmlFor="filter-species" className="form-label">
                  Pet Species
                </label>
                <select
                  id="filter-species"
                  value={selectedSpecies}
                  onChange={(e) => setSelectedSpecies(e.target.value)}
                  className="form-input"
                >
                  <option value="">All Species</option>
                  {speciesOptions.map(species => (
                    <option key={species} value={species}>{species}</option>
                  ))}
                </select>
              </div>

              {/* Price Range */}
              <div className="form-group">
                <label htmlFor="filter-price" className="form-label">
                  Price Range
                </label>
                <select
                  id="filter-price"
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  className="form-input"
                >
                  <option value="">Any Price</option>
                  {priceRanges.map(range => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>

              {/* Sort By */}
              <div className="form-group">
                <label htmlFor="filter-sort" className="form-label">
                  Sort By
                </label>
                <select
                  id="filter-sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="form-input"
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Active Filters */}
          {(serviceFilter || selectedSpecies || priceRange || location) && (
            <div className="mt-4 flex flex-wrap gap-2">
              {serviceFilter && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] font-medium">
                  {getServiceLabel(serviceFilter)}
                  <X
                    className="w-4 h-4 ml-2 cursor-pointer"
                    onClick={() => setServiceFilter('')}
                  />
                </span>
              )}
              {selectedSpecies && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] font-medium">
                  {selectedSpecies}
                  <X
                    className="w-4 h-4 ml-2 cursor-pointer"
                    onClick={() => setSelectedSpecies('')}
                  />
                </span>
              )}
              {priceRange && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] font-medium">
                  {priceRanges.find(r => r.value === priceRange)?.label}
                  <X
                    className="w-4 h-4 ml-2 cursor-pointer"
                    onClick={() => setPriceRange('')}
                  />
                </span>
              )}
              {location && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-[var(--pf-accent-soft)] text-[var(--pf-accent)] font-medium">
                  {location}
                  <X
                    className="w-4 h-4 ml-2 cursor-pointer"
                    onClick={() => setLocation('')}
                  />
                </span>
              )}
              <button
                onClick={clearFilters}
                className="text-sm text-[var(--pf-accent)] hover:opacity-80 font-medium"
              >
                Clear All
              </button>
            </div>
          )}
          </div>
        </ScrollReveal>

        {/* Results Header */}
        <ScrollReveal direction="left" className="results-bar mb-6">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-[var(--pf-accent-soft)]">
              <Filter className="w-4 h-4 text-[var(--pf-accent)]" />
            </span>
            <span className="text-[var(--pf-ink)] font-medium">
              {filteredCaregivers.length} caregiver{filteredCaregivers.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </ScrollReveal>

        {/* Caregivers Grid */}
        <DataFetchState
          loading={caregiversLoading}
          error={caregiversError}
          onRetry={fetchCaregivers}
          loadingLabel="Finding caregivers near you"
          errorTitle="Couldn't load caregivers"
        >
        {filteredCaregivers.length > 0 ? (
          <ScrollRevealStack className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" staggerMs={75}>
            {filteredCaregivers.map((caregiver) => (
              <CaregiverCard key={caregiver.id} caregiver={caregiver} />
            ))}
          </ScrollRevealStack>
        ) : (
          <ScrollReveal direction="right">
            <AnimatedEmptyState
            icon="loader-running-dog"
            title="No caregivers found"
            description={
              serviceFilter
                ? `No caregivers are offering ${getServiceLabel(serviceFilter).toLowerCase()} yet. Try another service or browse everyone.`
                : 'Try adjusting your search criteria or filters'
            }
            action={
              <button type="button" onClick={clearFilters} className="btn-outline">
                Clear All Filters
              </button>
            }
          />
          </ScrollReveal>
        )}
        </DataFetchState>

        {/* Browse by Service */}
        <ScrollReveal direction="left" className="section-panel mt-12">
          <h2 className="text-xl font-semibold text-[var(--pf-ink)] mb-4">Browse by Service</h2>
          <div className="flex flex-wrap gap-2">
            {SERVICE_CATALOG.map((service) => (
              <button
                key={service.slug}
                onClick={() => setServiceFilter(service.slug)}
                className={`px-4 py-2 rounded-full text-sm font-medium border-2 transition-colors ${
                  serviceFilter === service.slug
                    ? 'bg-[var(--pf-accent)] text-white border-[var(--pf-accent)]'
                    : 'bg-[var(--pf-surface)] border-[var(--pf-border)] hover:border-[var(--pf-accent)] hover:text-[var(--pf-accent)]'
                }`}
              >
                {service.label}
              </button>
            ))}
          </div>
        </ScrollReveal>

        {/* Disclaimer */}
        <ScrollReveal direction="right" className="mt-12 section-panel bg-[var(--pf-accent-soft)] border-[color-mix(in_srgb,var(--pf-accent)_25%,var(--pf-border))]">
          <p className="text-sm text-[var(--pf-ink-muted)] leading-relaxed">
            <strong className="text-[var(--pf-ink)]">Note:</strong> PetsFostering is a marketplace platform connecting pet owners with independent caregivers. All arrangements are made directly between users. We recommend meeting caregivers in person, discussing expectations, and verifying any credentials before finalizing arrangements.
          </p>
        </ScrollReveal>
      </div>
    </div>
  );
};

export default SearchCaregivers;